import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vendorAuthApi } from '@/services/vendor.api';
import { setVendorTokens, setVendorUser } from '@/lib/vendorAuth';

/** Accept a vendor invite (F5) — set a password, land signed in. */
export function VendorAcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await vendorAuthApi.acceptInvite(token, password, fullName.trim() || undefined);
      setVendorTokens(res.accessToken, res.refreshToken);
      setVendorUser(res.user);
      navigate('/vendor', { replace: true });
    } catch {
      setError('This invite is invalid or has expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 p-8"
      >
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-400">
          Vendor Observability
        </div>
        <h1 className="mb-6 text-lg font-semibold text-white">Accept your invite</h1>

        {!token && (
          <div className="mb-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
            Missing invite token.
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <label className="mb-1 block text-xs text-slate-400">Full name</label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="mb-4 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white"
        />
        <label className="mb-1 block text-xs text-slate-400">
          Password (min 10 chars)
        </label>
        <input
          type="password"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-6 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white"
        />
        <button
          type="submit"
          disabled={busy || !token}
          className="w-full rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
        >
          {busy ? 'Setting up…' : 'Accept invite'}
        </button>
      </form>
    </div>
  );
}
